import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { ScrollArea } from './ui/scroll-area';
import { Badge } from './ui/badge';
import { microphonesApi } from '../lib/api';
import { toast } from 'sonner';
import { Plus, Trash2, Mic, Radio, MicOff, User, ArrowRight } from 'lucide-react';

const MIC_TYPES = [
  { value: 'wireless', label: 'Inalámbrico', icon: '📡' },
  { value: 'lavalier', label: 'Lavalier/Solapa', icon: '🎙️' },
  { value: 'handheld', label: 'De mano', icon: '🎤' },
  { value: 'headset', label: 'Diadema', icon: '🎧' },
  { value: 'usb', label: 'USB/Condensador', icon: '🔌' },
  { value: 'shotgun', label: 'Shotgun', icon: '📺' },
];

export const MicrophonesDialog = ({ open, onOpenChange, eventId, microphones, speakers, onMicrophonesChange }) => {
  const [newMic, setNewMic] = useState({ name: '', type: 'wireless', channel: '', notes: '' });
  const [adding, setAdding] = useState(false);

  const handleAdd = async (e) => {
    e.preventDefault();
    if (!newMic.name.trim()) return;
    
    setAdding(true);
    try {
      const res = await microphonesApi.create(eventId, newMic);
      onMicrophonesChange([...microphones, res.data]);
      setNewMic({ name: '', type: 'wireless', channel: '', notes: '' });
      toast.success('Micrófono agregado');
    } catch (error) {
      toast.error('Error al agregar micrófono');
    } finally {
      setAdding(false);
    }
  };

  const handleAssign = async (micId, speakerId) => {
    try {
      const res = await microphonesApi.assign(eventId, micId, speakerId);
      onMicrophonesChange(microphones.map(m => m.id === micId ? res.data : m));
      toast.success(speakerId ? 'Micrófono asignado' : 'Micrófono liberado');
    } catch (error) {
      toast.error('Error al asignar');
    }
  };

  const handleDelete = async (micId) => {
    if (!window.confirm('¿Eliminar este micrófono?')) return;
    try {
      await microphonesApi.delete(eventId, micId);
      onMicrophonesChange(microphones.filter(m => m.id !== micId));
      toast.success('Micrófono eliminado');
    } catch (error) {
      toast.error('Error al eliminar');
    }
  };

  const getAssignedSpeaker = (mic) => {
    if (!mic.assigned_to) return null;
    return speakers.find(s => s.id === mic.assigned_to);
  };

  const getAvailableSpeakers = (currentMicId) => {
    // Speakers not assigned to any mic, or assigned to current mic
    const assignedSpeakerIds = microphones
      .filter(m => m.id !== currentMicId && m.assigned_to)
      .map(m => m.assigned_to);
    return speakers.filter(s => !assignedSpeakerIds.includes(s.id));
  };

  const freeMics = microphones.filter(m => !m.assigned_to);
  const assignedMics = microphones.filter(m => m.assigned_to);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh]">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            <Mic className="w-5 h-5" />
            Gestionar Micrófonos
          </DialogTitle>
        </DialogHeader>

        <div className="mt-4 space-y-6">
          {/* Status summary */}
          <div className="flex gap-4">
            <div className="flex-1 p-3 rounded-lg bg-green-500/10 border border-green-500/20">
              <div className="text-2xl font-bold text-green-500">{freeMics.length}</div>
              <div className="text-xs text-green-500/70">Disponibles</div>
            </div>
            <div className="flex-1 p-3 rounded-lg bg-red-500/10 border border-red-500/20">
              <div className="text-2xl font-bold text-red-500">{assignedMics.length}</div>
              <div className="text-xs text-red-500/70">En uso</div>
            </div>
            <div className="flex-1 p-3 rounded-lg bg-muted">
              <div className="text-2xl font-bold">{microphones.length}</div>
              <div className="text-xs text-muted-foreground">Total</div>
            </div>
          </div>

          {/* Add new microphone */}
          <form onSubmit={handleAdd} className="space-y-3 p-4 border border-dashed rounded-lg">
            <div className="text-sm font-medium text-muted-foreground">Agregar micrófono</div>
            <div className="grid grid-cols-4 gap-2">
              <div className="col-span-2 space-y-1">
                <Label className="text-xs">Nombre *</Label>
                <Input
                  placeholder="Ej: Mic 1, Lavalier A"
                  value={newMic.name}
                  onChange={(e) => setNewMic({ ...newMic, name: e.target.value })}
                  data-testid="new-mic-name"
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Tipo</Label>
                <Select value={newMic.type} onValueChange={(v) => setNewMic({ ...newMic, type: v })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {MIC_TYPES.map(t => (
                      <SelectItem key={t.value} value={t.value}>
                        {t.icon} {t.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Canal</Label>
                <Input
                  placeholder="Ch 1"
                  value={newMic.channel}
                  onChange={(e) => setNewMic({ ...newMic, channel: e.target.value })}
                />
              </div>
            </div>
            <Button type="submit" size="sm" disabled={adding || !newMic.name.trim()} className="w-full" data-testid="add-mic-btn">
              <Plus className="w-4 h-4 mr-2" />
              Agregar micrófono
            </Button>
          </form>

          {/* Microphones list */}
          <ScrollArea className="h-[300px]">
            {microphones.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <MicOff className="w-10 h-10 mx-auto mb-2 opacity-50" />
                <p className="text-sm">No hay micrófonos configurados</p>
              </div>
            ) : (
              <div className="space-y-2">
                {microphones.map(mic => {
                  const assignedSpeaker = getAssignedSpeaker(mic);
                  const availableSpeakers = getAvailableSpeakers(mic.id);
                  const micType = MIC_TYPES.find(t => t.value === mic.type);
                  
                  return (
                    <div
                      key={mic.id}
                      className={`flex items-center gap-3 p-3 rounded-lg border transition-colors ${
                        assignedSpeaker ? 'border-red-500/30 bg-red-500/5' : 'border-green-500/30 bg-green-500/5'
                      }`}
                      data-testid={`mic-item-${mic.id}`}
                    >
                      {/* Mic info */}
                      <div className="flex items-center gap-3 min-w-[180px]">
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center text-lg ${
                          assignedSpeaker ? 'bg-red-500/20' : 'bg-green-500/20'
                        }`}>
                          {micType?.icon || '🎤'}
                        </div>
                        <div>
                          <div className="font-medium flex items-center gap-2">
                            {mic.name}
                            {mic.channel && (
                              <Badge variant="outline" className="text-[10px]">{mic.channel}</Badge>
                            )}
                          </div>
                          <div className="text-xs text-muted-foreground">{micType?.label}</div>
                        </div>
                      </div>

                      {/* Assignment */}
                      <div className="flex-1 flex items-center gap-2">
                        <ArrowRight className="w-4 h-4 text-muted-foreground" />
                        
                        <Select
                          value={mic.assigned_to || "none"}
                          onValueChange={(value) => handleAssign(mic.id, value === "none" ? null : value)}
                        >
                          <SelectTrigger className={`w-[200px] ${assignedSpeaker ? 'border-red-500/50' : 'border-green-500/50'}`}>
                            <SelectValue>
                              {assignedSpeaker ? (
                                <div className="flex items-center gap-2">
                                  <div className="w-5 h-5 rounded-full bg-primary/10 flex items-center justify-center text-[10px] overflow-hidden">
                                    {assignedSpeaker.avatar_url ? (
                                      <img src={assignedSpeaker.avatar_url} alt="" className="w-full h-full object-cover" />
                                    ) : (
                                      assignedSpeaker.initials
                                    )}
                                  </div>
                                  <span>{assignedSpeaker.name}</span>
                                </div>
                              ) : (
                                <span className="text-green-500 flex items-center gap-1">
                                  <Radio className="w-3 h-3" />
                                  Disponible
                                </span>
                              )}
                            </SelectValue>
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">
                              <span className="text-green-500">✓ Marcar como disponible</span>
                            </SelectItem>
                            {availableSpeakers.map(s => (
                              <SelectItem key={s.id} value={s.id}>
                                <div className="flex items-center gap-2">
                                  <div className="w-5 h-5 rounded-full bg-primary/10 flex items-center justify-center text-[10px] overflow-hidden">
                                    {s.avatar_url ? (
                                      <img src={s.avatar_url} alt="" className="w-full h-full object-cover" />
                                    ) : (
                                      s.initials
                                    )}
                                  </div>
                                  {s.name}
                                </div>
                              </SelectItem>
                            ))}
                            {/* Also show currently assigned if re-selecting */}
                            {assignedSpeaker && !availableSpeakers.find(s => s.id === assignedSpeaker.id) && (
                              <SelectItem value={assignedSpeaker.id}>
                                <div className="flex items-center gap-2">
                                  <div className="w-5 h-5 rounded-full bg-primary/10 flex items-center justify-center text-[10px] overflow-hidden">
                                    {assignedSpeaker.avatar_url ? (
                                      <img src={assignedSpeaker.avatar_url} alt="" className="w-full h-full object-cover" />
                                    ) : (
                                      assignedSpeaker.initials
                                    )}
                                  </div>
                                  {assignedSpeaker.name} (actual)
                                </div>
                              </SelectItem>
                            )}
                          </SelectContent>
                        </Select>
                      </div>

                      {/* Delete button */}
                      <Button 
                        size="icon" 
                        variant="ghost" 
                        className="h-8 w-8 text-destructive hover:text-destructive"
                        onClick={() => handleDelete(mic.id)}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  );
                })}
              </div>
            )}
          </ScrollArea>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default MicrophonesDialog;
